/* eslint-disable @typescript-eslint/no-explicit-any */
import { type IFeatureFlags } from '@audacy-clients/core/atoms/config/settings';
import { type IBrazeInstance } from './@types/braze';
import type AnalyticServices from './analyticServices';
import type AudioServices from './audioServices';
import { type ApiVersion, type Environment } from './Constants';
import { type TConnectivityType } from './Container';
import { type ILogger } from './logger';
import type PersonalizationServices from './personalizationServices';

/**
 * Easier to use global config object for client services
 */
const clientServicesConfig = {} as IClientServicesConfig;
export default clientServicesConfig;
export type IClientServicesConfig = {
  analyticServices: Omit<
    ConstructorParameters<typeof AnalyticServices>[0],
    'eventSender' | 'logger' | 'marketingDataProvider' | 'networkStateProvider' | 'onlineStatusProvider'
  >;
  dataServices: {
    env: Environment;
    ver: ApiVersion;
  };
  personalizationServices: Omit<
    ConstructorParameters<typeof PersonalizationServices>[0],
    'logger' | 'profileApi' | 'experienceApi'
  >;
  audioServices: Omit<
    ConstructorParameters<typeof AudioServices>[0],
    | 'unauthenticatedProvider'
    | 'marketingDataProvider'
    | 'personalizationProvider'
    | 'analyticsProvider'
    | 'playerSettingsProvider'
    | 'credentialsProvider'
    | 'logger'
    | 'locationProvider'
    | 'dataProvider'
  >;
  /**
   * Used to pass a us_privacy string to ads to comply with privacy laws.
   */
  disableTargetedAds?: boolean;
  logger: ILogger;
  featureFlags?: IFeatureFlags;
  deviceInfoProvider?: IDeviceInfoProvider;
  callBrazeEvent?: IBrazeInstance['logCustomEvent'];
  removeFromBrazeAttributeArray?: IBrazeInstance['removeFromCustomUserAttributeArray'];
  addToBrazeAttributeArray?: IBrazeInstance['addToCustomUserAttributeArray'];
}

export type INetworkInfo = {
  bandwidthEstimateBPS?: number;
  historicBandwidthEstimateBPS?: Array<{
    bandwidth: number;
    timestamp: number;
  }>;
  latencyEstimate?: number;
  type: TConnectivityType;
}

export type IDeviceInfoProvider = {
  getNetworkInfo: () => Promise<INetworkInfo>;
  getAudioRoute: () => string;
}

/**
 * Returns "idfa:{token}" | "gaid:{token}" | "cookie:{token}"
 */
export type AdvertisingIdFunction = () => Promise<string>;

export enum AdvertisingIdAttsType {
  NOT_DETERMINED = '0',
  RESTRICTED = '1',
  DENIED = '2',
  AUTHORIZED = '3',
}
